import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, ExternalLink, Flag, Library, Link2, Loader2, Plus, Radio, RotateCcw, Trash2, Unlink, Users } from 'lucide-react';
import { api } from '../../services/api';
import { WelcomeEditor, WelcomeHub } from '../session-hub/WelcomeEditor';
import { LinkBookingModal } from './LinkBookingModal';
import { ObjectiveAndHopes } from '../session-hub/SessionAnswers';
import { useAuth } from '../../contexts/AuthContext';
import type { DeletionSchedule } from '../../hooks/useProjects';

type Stage = 'before' | 'live' | 'after';
type Mode = 'in_person' | 'remote_teams';

interface Row {
  sessionId: string;
  name: string;
  createdAt: string;
  roomName: string | null;
  leadFacilitator: string | null;
  stage: Stage | null;
  inHub: boolean;
  booking: { bookingId: string; customer: string; days: string[] } | null;
  attendees: number;
  sharedItems: number;
  breakouts: Row[];
}

interface Overview {
  hub: { hubId: string; title: string; status: string } | null;
  sessions: Row[];
}

interface Attendee {
  email: string;
  displayName: string | null;
  mode: Mode;
  source: 'facilitator' | 'am' | 'walk_in';
  visitorActive: boolean;
}

interface Visitor {
  email: string;
  displayName: string | null;
  status: 'active' | 'removed';
}

interface Props {
  projectId: string;
  /** Holds SessionHub::Manage: can add sessions and attendees */
  canManage: boolean;
  onOpenSession: (sessionId: string) => void;
  deletionSchedules?: Record<string, DeletionSchedule>;
}

const STAGE: Record<Stage, { label: string; cls: string }> = {
  before: { label: 'Before', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  live: { label: 'Live', cls: 'bg-green-50 text-green-600 border-green-200' },
  after: { label: 'After', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

/** The one next step a facilitator can take from each stage */
const NEXT: Record<Stage, { to: Stage; label: string; icon: React.ElementType; confirm?: string }> = {
  before: { to: 'live', label: 'Go live', icon: Radio },
  live: { to: 'after', label: 'End session', icon: Flag, confirm: 'End "{name}" in the Session Hub? Attendees move to the after-session view.' },
  after: { to: 'live', label: 'Reopen', icon: RotateCcw, confirm: 'Reopen "{name}" as live in the Session Hub?' },
};

const MODE_LABEL: Record<Mode, string> = { in_person: 'In person', remote_teams: 'Teams' };
const SOURCE_LABEL: Record<Attendee['source'], string> = { facilitator: 'Facilitator', am: 'Account manager', walk_in: 'Walk-in' };

const fmtDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const errorOf = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

const th = 'text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap';
const td = 'px-4 py-3 text-sm text-slate-600 whitespace-nowrap';
const smallBtn = 'flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-60';

function Attendees({ projectId, row, hubId, canManage, onChanged }: { projectId: string; row: Row; hubId: string | null; canManage: boolean; onChanged: () => void }) {
  const base = `/api/projects/${projectId}/session-hub/sessions/${row.sessionId}`;
  const [list, setList] = useState<Attendee[] | null>(null);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [pick, setPick] = useState('');
  const [mode, setMode] = useState<Mode>('in_person');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList((await api.get<{ attendees: Attendee[] }>(`${base}/attendees`)).data.attendees);
    } catch (e) {
      setError(errorOf(e, 'Could not load attendees'));
      setList([]);
    }
  }, [base]);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);
  useEffect(() => {
    if (!canManage || !hubId) return;
    api.get<{ visitors: Visitor[] }>(`/api/projects/${projectId}/session-hubs/${hubId}/visitors`)
      .then((r) => setVisitors(r.data.visitors.filter((v) => v.status === 'active')))
      .catch(() => {});
  }, [canManage, hubId, projectId]);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (e) {
      setError(errorOf(e, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  }

  if (!canManage) {
    return <p className="text-xs text-slate-400">{row.attendees} attending. Ask a Session Hub manager to see who.</p>;
  }
  if (!hubId) return <p className="text-xs text-slate-400">Create this project's Session Hub to add attendees.</p>;

  const attending = new Set((list ?? []).map((a) => a.email));
  const options = visitors.filter((v) => !attending.has(v.email));

  return (
    <div className="flex flex-col gap-3">
      {error && <p className="text-xs font-medium text-red-700 flex items-center gap-1.5"><AlertCircle size={14} /> {error}</p>}
      {list === null ? (
        <Loader2 size={16} className="animate-spin text-slate-400" />
      ) : list.length === 0 ? (
        <p className="text-xs text-slate-400">No attendees yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white">
          {list.map((a) => (
            <li key={a.email} className="px-3 py-2 flex flex-wrap items-center gap-3">
              <span className="flex-1 min-w-[12rem]">
                <span className={`block text-sm font-semibold ${a.visitorActive ? 'text-navy' : 'text-slate-400 line-through'}`}>{a.displayName || a.email}</span>
                <span className="block text-xs text-slate-400">{a.email} · {SOURCE_LABEL[a.source]}{a.visitorActive ? '' : ' · removed from the hub'}</span>
              </span>
              <select
                aria-label={`Attendance for ${a.email}`} value={a.mode} disabled={!!busy}
                onChange={(e) => run(a.email, () => api.put(`${base}/attendees/${encodeURIComponent(a.email)}`, { mode: e.target.value }))}
                className="border border-[#e2e8f0] rounded-lg h-8 px-2 text-xs text-navy bg-white"
              >
                {(Object.keys(MODE_LABEL) as Mode[]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
              </select>
              <button
                type="button" title="Remove from this session" disabled={!!busy}
                onClick={() => run(a.email, () => api.delete(`${base}/attendees/${encodeURIComponent(a.email)}`))}
                className="text-slate-400 hover:text-red-600"
              >
                {busy === a.email ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </li>
          ))}
        </ul>
      )}
      {options.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Hub visitor to add" value={pick} onChange={(e) => setPick(e.target.value)} className="border border-[#e2e8f0] rounded-lg h-8 px-2 text-xs text-navy bg-white min-w-[14rem]">
            <option value="">Add a hub visitor…</option>
            {options.map((v) => <option key={v.email} value={v.email}>{v.displayName ? `${v.displayName} (${v.email})` : v.email}</option>)}
          </select>
          <select aria-label="Attendance" value={mode} onChange={(e) => setMode(e.target.value as Mode)} className="border border-[#e2e8f0] rounded-lg h-8 px-2 text-xs text-navy bg-white">
            {(Object.keys(MODE_LABEL) as Mode[]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
          </select>
          <button
            type="button" className={smallBtn} disabled={!pick || !!busy}
            onClick={() => run('add', async () => { await api.put(`${base}/attendees/${encodeURIComponent(pick)}`, { mode }); setPick(''); })}
          >
            {busy === 'add' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
          </button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Everyone on the hub is attending, or the hub has no visitors yet. Add people in the Session Hub tab.</p>
      )}
    </div>
  );
}

/** The session's own welcome message and video, shown to its attendees alongside the hub's welcome. */
function SessionWelcomeSection({ projectId, sessionId }: { projectId: string; sessionId: string }) {
  const base = `/api/projects/${projectId}/session-hub/sessions/${sessionId}`;
  const [view, setView] = useState<WelcomeHub | null>(null);
  const [error, setError] = useState<string | null>(null);
  const call = useCallback(async <T,>(path: string, method = 'GET', data?: unknown): Promise<T> => {
    try {
      return (await api.request<T>({ url: `${base}${path}`, method, data })).data;
    } catch (e) {
      throw new Error(errorOf(e, 'Request failed'));
    }
  }, [base]);
  useEffect(() => {
    call<{ hub: WelcomeHub }>('').then((r) => setView(r.hub)).catch((e) => setError(e.message));
  }, [call]);
  if (error) return <p className="text-xs text-red-700">{error}</p>;
  if (!view) return <Loader2 size={16} className="animate-spin text-slate-400" />;
  return <WelcomeEditor hub={view} call={call} onChanged={setView} />;
}

export function SessionsTab({ projectId, canManage, onOpenSession, deletionSchedules }: Props) {
  const { can } = useAuth();
  const canLinkBooking = can('Studio::LinkBookingSession');
  const [linking, setLinking] = useState<Row | null>(null);
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get<Overview>(`/api/projects/${projectId}/sessions-overview`)).data);
      setError(null);
    } catch (e) {
      setError(errorOf(e, 'Could not load sessions'));
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function moveStage(row: Row) {
    const next = NEXT[row.stage!];
    if (next.confirm && !window.confirm(next.confirm.replace('{name}', row.name))) return;
    setBusy(row.sessionId);
    try {
      await api.put(`/api/projects/${projectId}/session-hub/sessions/${row.sessionId}/stage`, { stage: next.to });
      await load();
    } catch (e) {
      setError(errorOf(e, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  }

  async function unlinkBooking(row: Row) {
    if (!window.confirm(`Unlink "${row.name}" from the ${row.booking!.customer} booking? Its attendees stay on the session.`)) return;
    setBusy(row.sessionId);
    try {
      await api.delete(`/api/projects/${projectId}/sessions/${row.sessionId}/booking`);
      await load();
    } catch (e) {
      setError(errorOf(e, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  }

  async function setInHub(row: Row, add: boolean) {
    if (!add && !window.confirm(`Take "${row.name}" off the Session Hub? Its attendee list is removed; shared items stay.`)) return;
    setBusy(row.sessionId);
    try {
      if (add) await api.post(`/api/projects/${projectId}/session-hub/sessions`, { sessionId: row.sessionId });
      else await api.delete(`/api/projects/${projectId}/session-hub/sessions/${row.sessionId}`);
      await load();
    } catch (e) {
      setError(errorOf(e, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  }

  if (error && !data) {
    return <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"><AlertCircle size={16} /> {error}</div>;
  }
  if (!data) return <div className="flex justify-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>;
  if (data.sessions.length === 0) {
    return <div className="rounded-xl bg-white p-12 border border-slate-200 text-center text-sm text-slate-500">No sessions in this project yet.</div>;
  }

  const hubOpen = data.hub?.status === 'active';

  return (
    <div className="flex flex-col gap-4">
      {linking && (
        <LinkBookingModal
          projectId={projectId} sessionId={linking.sessionId} sessionName={linking.name}
          onClose={() => setLinking(null)} onLinked={() => { setLinking(null); load(); }}
        />
      )}
      {error && <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"><AlertCircle size={16} /> {error}</div>}
      {!hubOpen && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Library size={14} /> {data.hub ? `The Session Hub "${data.hub.title}" is closed.` : 'This project has no Session Hub yet.'} Hub columns fill in once it is open.
        </p>
      )}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-[#e2e8f0] bg-slate-50/60">
            <tr>
              <th className={th} aria-label="Expand" />
              <th className={th}>Session</th>
              <th className={th}>Stage</th>
              <th className={th}>Room</th>
              <th className={th}>Facilitator</th>
              <th className={th}>Attendees</th>
              <th className={th}>Shared</th>
              <th className={th}>Deletion</th>
              <th className={th} aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8f0]">
            {data.sessions.map((row) => {
              const expanded = open.has(row.sessionId);
              const schedule = deletionSchedules?.[row.sessionId];
              return (
                <Fragment key={row.sessionId}>
                  <tr className="hover:bg-slate-50/60">
                    <td className="pl-4 py-3 w-8">
                      <button type="button" onClick={() => toggle(row.sessionId)} aria-expanded={expanded} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.name}`} className="text-slate-400 hover:text-primary">
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td className={td}>
                      <span className="block font-semibold text-navy">{row.name}</span>
                      <span className="block text-xs text-slate-400">
                        {row.booking
                          ? <>Booking: {row.booking.customer} · {row.booking.days.map(fmtDay).join(', ')}</>
                          : <>Created {fmtDate(row.createdAt)}</>}
                      </span>
                    </td>
                    <td className={td}>{row.stage ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STAGE[row.stage].cls}`}>{STAGE[row.stage].label}</span> : <span className="text-slate-300">—</span>}</td>
                    <td className={td}>{row.roomName ?? <span className="text-slate-300">—</span>}</td>
                    <td className={td}>{row.leadFacilitator ?? <span className="text-slate-300">—</span>}</td>
                    <td className={td}>{row.inHub ? <span className="flex items-center gap-1"><Users size={14} className="text-slate-400" /> {row.attendees}</span> : <span className="text-slate-300">Not on hub</span>}</td>
                    <td className={td}>{row.sharedItems || <span className="text-slate-300">0</span>}</td>
                    <td className={td}>{schedule ? <span className="text-xs font-semibold text-red-600">{fmtDate(`${schedule.deleteOn}T12:00:00Z`)}</span> : <span className="text-slate-300">—</span>}</td>
                    <td className={`${td} text-right`}>
                      <div className="flex justify-end gap-2">
                        {canManage && hubOpen && row.stage && (() => {
                          const next = NEXT[row.stage];
                          return (
                            <button type="button" className={smallBtn} disabled={busy === row.sessionId} onClick={() => moveStage(row)}>
                              <next.icon size={14} /> {next.label}
                            </button>
                          );
                        })()}
                        {canManage && hubOpen && (
                          <button type="button" className={smallBtn} disabled={busy === row.sessionId} onClick={() => setInHub(row, !row.inHub)}>
                            {busy === row.sessionId ? <Loader2 size={14} className="animate-spin" /> : row.inHub ? <Trash2 size={14} /> : <Plus size={14} />}
                            {row.inHub ? 'Off hub' : 'Add to hub'}
                          </button>
                        )}
                        {canLinkBooking && (row.booking ? (
                          <button type="button" className={smallBtn} disabled={busy === row.sessionId} onClick={() => unlinkBooking(row)} title={`Linked to the ${row.booking.customer} booking`}>
                            <Unlink size={14} /> Unlink booking
                          </button>
                        ) : (
                          <button type="button" className={smallBtn} onClick={() => setLinking(row)}><Link2 size={14} /> Link to booking</button>
                        ))}
                        <button type="button" className={smallBtn} onClick={() => onOpenSession(row.sessionId)}><ExternalLink size={14} /> Open</button>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-slate-50/50">
                      <td />
                      <td colSpan={8} className="px-4 py-4">
                        <div className="flex flex-col gap-4">
                          <div>
                            <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">Attendees</p>
                            <Attendees projectId={projectId} row={row} hubId={hubOpen ? data.hub!.hubId : null} canManage={canManage} onChanged={load} />
                          </div>
                          {hubOpen && row.inHub && <ObjectiveAndHopes projectId={projectId} sessionId={row.sessionId} canManage={canManage} />}
                          {canManage && hubOpen && row.inHub && (
                            <div>
                              <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">Session welcome</p>
                              <p className="text-xs text-slate-400 mb-2">Attendees see this on the session's page, as well as the hub's welcome.</p>
                              <SessionWelcomeSection projectId={projectId} sessionId={row.sessionId} />
                            </div>
                          )}
                          <div>
                            <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">Breakouts</p>
                            {row.breakouts.length === 0 ? (
                              <p className="text-xs text-slate-400">No breakouts.</p>
                            ) : (
                              <ul className="text-sm text-slate-600">{row.breakouts.map((b) => <li key={b.sessionId}>{b.name}{b.stage ? ` · ${STAGE[b.stage].label}` : ''}</li>)}</ul>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
